import { useState } from "react";
import { useCampaigns, useDeleteCampaign } from "../api/campaigns";
import { CampaignForm } from "./CampaignForm";
import { FlyerTypesPanel } from "./FlyerTypesPanel";
import { showError, confirmAction } from "../shared/toast";
import type { Campaign } from "../api/campaigns";
import "./campaigns-panel.css";

export const CampaignsPanel = (): JSX.Element => {
    const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
    const { data: campaigns = [], isLoading, error } = useCampaigns();
    const deleteMutation = useDeleteCampaign();

    const handleEdit = (campaign: Campaign) => {
        setEditingCampaign(campaign);
        setShowCreateForm(false);
        setSelectedCampaignId(campaign.id);
    };

    const handleDelete = async (id: string) => {
        const confirmed = await confirmAction("Är du säker på att du vill ta bort denna kampanj?");
        if (!confirmed) {
            return;
        }

        try {
            await deleteMutation.mutateAsync(id);
        } catch (error: any) {
            const message =
                error?.response?.data?.message ||
                "Kunde inte ta bort kampanjen. Kontrollera att inga aktiva assignments finns.";
            showError("Kunde inte ta bort kampanjen", message);
        }
    };

    const handleFormSuccess = () => {
        setEditingCampaign(null);
        setShowCreateForm(false);
        // Keep selectedCampaignId if we just created/edited a campaign
    };

    const handleFormCancel = () => {
        setEditingCampaign(null);
        setShowCreateForm(false);
    };

    const handleViewFlyerTypes = (campaign: Campaign) => {
        setSelectedCampaignId(campaign.id);
        setEditingCampaign(null);
        setShowCreateForm(false);
    };

    if (error) {
        return (
            <div className="campaigns-panel">
                <h2>Kampanjer</h2>
                <p className="error-message">Kunde inte ladda kampanjer.</p>
            </div>
        );
    }

    return (
        <section className="campaigns-panel" aria-label="Kampanjer">
            <div className="campaigns-panel__header">
                <h2>Kampanjer</h2>
                <button
                    className="button button--primary"
                    onClick={() => {
                        setShowCreateForm(true);
                        setEditingCampaign(null);
                    }}
                    aria-label="Skapa ny kampanj"
                >
                    + Skapa ny kampanj
                </button>
            </div>

            {(showCreateForm || editingCampaign) && (
                <div className="campaigns-panel__form">
                    <CampaignForm
                        campaign={editingCampaign || undefined}
                        onSuccess={handleFormSuccess}
                        onCancel={handleFormCancel}
                    />
                </div>
            )}

            {isLoading ? (
                <p>Laddar...</p>
            ) : (
                <div className="campaigns-panel__list">
                    {campaigns.length === 0 ? (
                        <p>Inga kampanjer hittades.</p>
                    ) : (
                        <table className="campaigns-table">
                            <thead>
                                <tr>
                                    <th>Namn</th>
                                    <th>Beskrivning</th>
                                    <th>Startdatum</th>
                                    <th>Slutdatum</th>
                                    <th>Skapad av</th>
                                    <th>Åtgärder</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map((campaign) => (
                                    <tr key={campaign.id}>
                                        <td>{campaign.name}</td>
                                        <td>{campaign.description || "-"}</td>
                                        <td>
                                            {campaign.startDate
                                                ? new Date(campaign.startDate).toLocaleDateString("sv-SE")
                                                : "-"}
                                        </td>
                                        <td>
                                            {campaign.endDate
                                                ? new Date(campaign.endDate).toLocaleDateString("sv-SE")
                                                : "-"}
                                        </td>
                                        <td>{campaign.createdBy || "-"}</td>
                                        <td>
                                            <div className="campaigns-table__actions">
                                                {campaign.canManage !== false && (
                                                    <>
                                                        <button
                                                            className="button button--secondary"
                                                            onClick={() => handleEdit(campaign)}
                                                            aria-label={`Redigera ${campaign.name}`}
                                                        >
                                                            Redigera
                                                        </button>
                                                        <button
                                                            className="button button--secondary"
                                                            onClick={() => handleViewFlyerTypes(campaign)}
                                                            aria-label={`Hantera flygblad för ${campaign.name}`}
                                                        >
                                                            Flygblad
                                                        </button>
                                                        <button
                                                            className="button button--danger"
                                                            onClick={() => handleDelete(campaign.id)}
                                                            disabled={deleteMutation.isPending}
                                                            aria-label={`Ta bort ${campaign.name}`}
                                                        >
                                                            {deleteMutation.isPending ? "Tar bort..." : "Ta bort"}
                                                        </button>
                                                    </>
                                                )}
                                                {campaign.canManage === false && (
                                                    <span className="campaigns-table__readonly-hint">Endast visning</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {selectedCampaignId && (
                <FlyerTypesPanel
                    campaignId={selectedCampaignId}
                    campaignName={
                        campaigns.find((c) => c.id === selectedCampaignId)?.name || ""
                    }
                />
            )}
        </section>
    );
};